// @ts-strict-ignore
import React from 'react';
import { AppComponentProps } from '../../typescriptHelpers';
import Text from 'antd/lib/typography/Text';
import Descriptions from 'antd/lib/descriptions';
import Space from 'antd/lib/space';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { SettingOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import {
    selectAppPlatform,
    selectAppStoreType,
    selectDestinationInfoByDestinationStepConfigId,
    selectIsWorkflowsCIStepEnabled,
    selectStepGraph,
    selectWorkflowEnabledSteps,
    selectWorkflowId,
} from '@/selectors/selectors';
import { StepArchetypeGroup } from '@/types/workflow';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { AppPlatform, AppStoreType } from '@/types';
import { antGrey } from '../styles/globalStyles';
import Divider from 'antd/lib/divider';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { Destination } from '@/types/destinations';
import { StepConfigSettingsDrawer } from '@/components/stepConfigs/StepConfigSettingsDrawer';

interface WorkflowStepsSectionProps {
    archetypeGroup: StepArchetypeGroup;
    onEditClicked: (stepConfigId: string) => void;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    destinationInfoByDestinationStepConfigId: Record<string, Destination>;
    canEditSettings: boolean;
    isWorkflowsCIStepEnabled: boolean;
}

const WorkflowStepsSection: React.FC<WorkflowStepsSectionProps> = ({
    archetypeGroup,
    onEditClicked,
    platform,
    appStoreType,
    destinationInfoByDestinationStepConfigId,
    canEditSettings,
    isWorkflowsCIStepEnabled,
}) => {
    const stepArchetypeDisplayText = displayTextForReleaseStepType(
        archetypeGroup.archetype,
        platform,
        appStoreType,
        isWorkflowsCIStepEnabled,
    );
    return (
        <Descriptions
            size="middle"
            layout="horizontal"
            bordered
            column={1}
            style={{ marginBottom: 24 }}
            labelStyle={{ width: 200 }}
        >
            <Descriptions.Item
                labelStyle={{ alignContent: 'start', padding: '18px 24px' }}
                label={
                    <Text strong style={{ color: antGrey.grey8 }}>
                        {stepArchetypeDisplayText}
                    </Text>
                }
                contentStyle={{ padding: '12px 0' }}
            >
                <Space
                    direction="vertical"
                    size={10}
                    style={{ width: '100%' }}
                    split={<Divider style={{ margin: 0 }} />}
                >
                    {archetypeGroup.steps.map((step) => {
                        const destinations = step.destinationStepConfigIds
                            .map(
                                (destinationStepConfigId) =>
                                    destinationInfoByDestinationStepConfigId[destinationStepConfigId],
                            )
                            .filter((destination) => destination !== undefined);

                        return (
                            <div
                                key={step.stepConfigId}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '0 20px',
                                }}
                            >
                                <Space size={16}>
                                    <DestinationCounter size={20} destinations={destinations} />
                                    <Text>{step.stepName}</Text>
                                </Space>
                                {canEditSettings && (
                                    <PermissionedActionButton
                                        allowDemoMode
                                        actionType={RunwayUserAction.UAUpdateAppSettings}
                                        type="link"
                                        icon={<SettingOutlined />}
                                        onClick={() => onEditClicked(step.stepConfigId)}
                                    />
                                )}
                            </div>
                        );
                    })}
                </Space>
            </Descriptions.Item>
        </Descriptions>
    );
};

type WorkflowStepsSettingsProps = AppComponentProps & {
    platform: AppPlatform;
    appStoreType: AppStoreType;
};

export const WorkflowStepsSettings: React.FC<WorkflowStepsSettingsProps> = ({ appId, orgId }) => {
    const [selectedStepConfigId, setSelectedStepConfigId] = React.useState<string | null>(null);
    const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

    const onEditClicked = (stepConfigId: string) => {
        setSelectedStepConfigId(stepConfigId);
        setIsDrawerOpen(true);
    };

    const platform = useSelector((state: RootState) => selectAppPlatform(state, appId));
    const appStoreType = useSelector((state: RootState) => selectAppStoreType(state, appId));
    const isWorkflowsCIStepEnabled = useSelector((state: RootState) => selectIsWorkflowsCIStepEnabled(state, appId));
    const workflowId = useSelector((state: RootState) => selectWorkflowId(state, appId));
    const workflowSteps = useSelector((state: RootState) => selectStepGraph(state, appId));

    const destinationInfoByDestinationStepConfigId = useSelector((state: RootState) =>
        selectDestinationInfoByDestinationStepConfigId(state, appId),
    );

    const workflowEnabledSteps = useSelector((state: RootState) => selectWorkflowEnabledSteps(state, appId));

    // Find the selected step for drawer props
    const selectedStep = React.useMemo(() => {
        if (!selectedStepConfigId || !workflowSteps) return null;

        for (const stepGroup of workflowSteps.stepGroups) {
            for (const archetypeGroup of stepGroup.archetypeGroups) {
                const step = archetypeGroup.steps.find((s) => s.stepConfigId === selectedStepConfigId);
                if (step) {
                    return { step, archetype: archetypeGroup.archetype };
                }
            }
        }
        return null;
    }, [selectedStepConfigId, workflowSteps]);

    return (
        <>
            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                {workflowSteps?.stepGroups.map((stepGroup, groupIndex) =>
                    stepGroup.archetypeGroups.map((archetypeGroup, archetypeIndex) => (
                        <WorkflowStepsSection
                            platform={platform}
                            appStoreType={appStoreType}
                            key={`${groupIndex}-${archetypeIndex}`}
                            archetypeGroup={archetypeGroup}
                            onEditClicked={onEditClicked}
                            destinationInfoByDestinationStepConfigId={destinationInfoByDestinationStepConfigId}
                            canEditSettings={workflowEnabledSteps[archetypeGroup.archetype]}
                            isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                        />
                    )),
                )}
            </Space>
            <StepConfigSettingsDrawer
                key={selectedStepConfigId}
                appId={appId}
                orgId={orgId}
                stepConfigId={selectedStepConfigId!}
                workflowId={workflowId}
                isOpen={isDrawerOpen}
                releaseStepTypeText={displayTextForReleaseStepType(
                    selectedStep?.archetype,
                    platform,
                    appStoreType,
                    isWorkflowsCIStepEnabled,
                )}
                stepName={selectedStep?.step.stepName}
                onClose={() => {
                    setIsDrawerOpen(false);
                }}
            />
        </>
    );
};
